/**
 * useRangeFilters Hook
 *
 * Manages range filter state for dashboard filtering.
 * Handles days until stockout and inventory level range filters.
 * Provides validated input handlers that only accept positive integers.
 *
 * @example
 * ```tsx
 * const rangeFilters = useRangeFilters();
 *
 * <TextField
 *   value={rangeFilters.daysMin}
 *   onChange={rangeFilters.handleDaysMinChange}
 *   error={rangeFilters.daysRangeError}
 * />
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import { createRangeHandler, validateRange } from '../../utils/range-filter';

/**
 * Range filter state for days and inventory filters
 */
export interface RangeFilterState {
  daysMin: string;
  daysMax: string;
  inventoryMin: string;
  inventoryMax: string;
}

/**
 * Initial values for range filters (optional)
 */
export interface UseRangeFiltersOptions {
  initialDaysMin?: string;
  initialDaysMax?: string;
  initialInventoryMin?: string;
  initialInventoryMax?: string;
}

/**
 * Return type for useRangeFilters hook
 */
export interface UseRangeFiltersReturn {
  // State values
  daysMin: string;
  daysMax: string;
  inventoryMin: string;
  inventoryMax: string;

  // Validation errors
  daysRangeError: boolean;
  inventoryRangeError: boolean;

  // Direct setters (for external control)
  setDaysMin: (value: string) => void;
  setDaysMax: (value: string) => void;
  setInventoryMin: (value: string) => void;
  setInventoryMax: (value: string) => void;

  // Validated change handlers (for TextField onChange)
  handleDaysMinChange: (value: string) => void;
  handleDaysMaxChange: (value: string) => void;
  handleInventoryMinChange: (value: string) => void;
  handleInventoryMaxChange: (value: string) => void;

  // Clear all filters
  clearAllFilters: () => void;

  // Check if any filters are active
  hasActiveFilters: boolean;
}

/**
 * Hook for managing range filter state
 *
 * @param options - Optional initial values for filters
 * @returns Range filter state, handlers, and validation
 */
export function useRangeFilters(
  options: UseRangeFiltersOptions = {}
): UseRangeFiltersReturn {
  const {
    initialDaysMin = '',
    initialDaysMax = '',
    initialInventoryMin = '',
    initialInventoryMax = '',
  } = options;

  // State
  const [daysMin, setDaysMin] = useState<string>(initialDaysMin);
  const [daysMax, setDaysMax] = useState<string>(initialDaysMax);
  const [inventoryMin, setInventoryMin] = useState<string>(initialInventoryMin);
  const [inventoryMax, setInventoryMax] = useState<string>(initialInventoryMax);

  // Validation - min must be less than max
  const daysRangeError = useMemo(
    () => validateRange(daysMin, daysMax),
    [daysMin, daysMax]
  );

  const inventoryRangeError = useMemo(
    () => validateRange(inventoryMin, inventoryMax),
    [inventoryMin, inventoryMax]
  );

  // Validated change handlers - only allow empty string or positive integers
  const handleDaysMinChange = useCallback(
    createRangeHandler(setDaysMin),
    []
  );

  const handleDaysMaxChange = useCallback(
    createRangeHandler(setDaysMax),
    []
  );

  const handleInventoryMinChange = useCallback(
    createRangeHandler(setInventoryMin),
    []
  );

  const handleInventoryMaxChange = useCallback(
    createRangeHandler(setInventoryMax),
    []
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setDaysMin('');
    setDaysMax('');
    setInventoryMin('');
    setInventoryMax('');
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () => !!(daysMin || daysMax || inventoryMin || inventoryMax),
    [daysMin, daysMax, inventoryMin, inventoryMax]
  );

  return {
    // State values
    daysMin,
    daysMax,
    inventoryMin,
    inventoryMax,

    // Validation errors
    daysRangeError,
    inventoryRangeError,

    // Direct setters
    setDaysMin,
    setDaysMax,
    setInventoryMin,
    setInventoryMax,

    // Validated change handlers
    handleDaysMinChange,
    handleDaysMaxChange,
    handleInventoryMinChange,
    handleInventoryMaxChange,

    // Clear all
    clearAllFilters,

    // Has active filters
    hasActiveFilters,
  };
}
